// ===================================
// AUTH CONTEXT - FIXED
// src/context/AuthContext.js
// Supports both credential login AND OAuth/token login
// ===================================

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize auth on mount
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('token');
      
      if (token) {
        try {
          const response = await api.get('/auth/me');
          // Handle both response formats
          const userData = response.data.user || response.data;
          setUser(userData);
        } catch (error) {
          console.error('Auto-login failed:', error);
          localStorage.removeItem('token');
        }
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  // ===================================
  // LOGIN WITH CREDENTIALS
  // For traditional email/password login
  // Makes API call to /auth/login
  // ===================================
  const login = async (credentials) => {
    try {
      setError(null);
      
      // Handle both object format { email, password } and separate args
      let email, password;
      if (typeof credentials === 'object' && credentials !== null) {
        email = credentials.email;
        password = credentials.password;
      } else {
        // Shouldn't happen, but handle gracefully
        throw new Error('Invalid credentials format');
      }

      const response = await api.post('/auth/login', { email, password });
      
      // Handle different response formats
      const token = response.data.token || response.data.access_token;
      const userData = response.data.user;

      if (!token) {
        throw new Error('No token received');
      }

      localStorage.setItem('token', token);
      setUser(userData);

      console.log('✅ Login successful:', userData?.email || userData?.username);
      return { success: true, user: userData };
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Login failed';
      setError(message);
      console.error('❌ Login failed:', message);
      return { success: false, error: message, authProvider: err.response?.data?.authProvider };
    }
  };

  // ===================================
  // SET AUTH DIRECTLY
  // For OAuth (Google), magic links, and registration
  // Does NOT make API call - just stores token and user
  // ===================================
  const setAuth = useCallback((token, userData) => {
    if (!token || !userData) {
      console.error('setAuth called without token or user data');
      return;
    }

    localStorage.setItem('token', token);
    setUser(userData);
    setError(null);

    console.log('✅ Auth set directly:', userData?.email || userData?.username);
  }, []);

  // ===================================
  // REGISTER
  // For new user registration
  // Makes API call to /auth/register
  // ===================================
  const register = async (userData) => {
    try {
      setError(null);
      const response = await api.post('/auth/register', userData);
      
      // Handle different response formats
      const token = response.data.token || response.data.access_token;
      const newUser = response.data.user;

      if (!token) {
        throw new Error('No token received');
      }

      localStorage.setItem('token', token);
      setUser(newUser);

      console.log('✅ Registration successful:', newUser?.email || newUser?.username);
      return { success: true, user: newUser };
    } catch (err) {
      const message = err.response?.data?.message || 'Registration failed';
      const errorData = err.response?.data || {};
      setError(message);
      return { 
        success: false, 
        error: message,
        verificationExpired: errorData.verificationExpired,
        requiresVerification: errorData.requiresVerification
      };
    }
  };

  // ===================================
  // LOGOUT
  // Clears token and user state
  // ===================================
  const logout = useCallback(() => {
    // Notify server (don't wait for response)
    api.post('/auth/logout').catch(() => {});
    
    localStorage.removeItem('token');
    setUser(null);
    setError(null);
    
    console.log('👋 User logged out');
  }, []);

  // ===================================
  // UPDATE USER
  // For updating user data locally after profile changes
  // ===================================
  const updateUser = useCallback((updates) => {
    setUser(prev => prev ? { ...prev, ...updates } : null);
  }, []);

  // ===================================
  // REFRESH USER
  // Fetch latest user data from server
  // ===================================
  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await api.get('/auth/me');
      const userData = response.data.user || response.data;
      setUser(userData);
      return userData;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      return null;
    }
  }, []);

  // ===================================
  // PERMISSION CHECK
  // Check if user has specific permission
  // ===================================
  const hasPermission = useCallback((permission) => {
    if (!user) return false;
    if (user.is_admin) return true;
    if (!user.permissions) return false;
    return user.permissions.includes(permission) || user.permissions.includes('full_access');
  }, [user]);

  // Clear error
  const clearError = useCallback(() => setError(null), []);

  const value = {
    user,
    isLoading,
    error,
    isAuthenticated: !!user,
    // Auth methods
    login,           // For email/password login (makes API call)
    setAuth,         // For OAuth/magic link (no API call, just stores)
    register,        // For new registration
    logout,
    // User methods
    updateUser,
    refreshUser,
    hasPermission,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;